import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Coins, LogOut, Moon, Sun } from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '@/supabaseClient'
import { useAuth } from '@/contexts/AuthContext'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/utils/cn'
import VoiceNav from './VoiceNav'

function formatCredits(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))
}

export default function VoiceHeader({ className }) {
  const { profile, attendant, signOut, getProfileId } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const attendantName = attendant?.name || attendant?.email || null
  const accountName = profile?.full_name || profile?.email || null
  const [creditsBalance, setCreditsBalance] = useState(0)

  useEffect(() => {
    const profileId = getProfileId()
    if (!profileId) {
      setCreditsBalance(0)
      return
    }

    let cancelled = false
    async function loadCredits() {
      const { data, error } = await supabase
        .from('credits')
        .select('current_balance')
        .eq('profile_id', profileId)
        .maybeSingle()

      if (cancelled) return
      if (error) {
        console.error('[VoiceHeader] credits:', error)
        return
      }
      setCreditsBalance(Number(data?.current_balance || 0))
    }

    loadCredits()
    const timer = setInterval(loadCredits, 60000)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [getProfileId])

  async function handleSignOut() {
    try {
      await signOut()
      toast.success('Sessão encerrada')
    } catch (e) {
      toast.error(e?.message || 'Erro ao sair')
    }
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full min-w-0 border-b border-border/80 bg-card/90 backdrop-blur-md supports-[backdrop-filter]:bg-card/75',
        className
      )}
    >
      <div className="mx-auto flex h-14 w-full min-w-0 max-w-[1600px] items-center justify-between gap-3 px-4 sm:gap-4 lg:px-8">
        <div className="flex min-w-0 flex-1 items-center gap-4 sm:gap-8">
          <Link
            to="/"
            className="group flex min-w-0 shrink-0 items-center gap-0 touch-manipulation"
          >
            <img
              src="/icon-sm.png"
              alt="WappDesk"
              className="h-9 w-9 shrink-0 object-contain transition-transform group-hover:scale-[1.02]"
              loading="lazy"
            />
            <span className="truncate -mt-0.5 font-semibold tracking-tight text-foreground">
              <span className="text-[17px] sm:text-lg">WappDesk</span>{' '}
              <span className="text-[11px] font-medium text-muted-foreground sm:text-xs">Voice</span>
            </span>
          </Link>

          <VoiceNav />
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <div className="hidden items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground lg:inline-flex">
            <Coins className="h-3.5 w-3.5 text-primary" />
            <span>{formatCredits(creditsBalance)}</span>
          </div>
          {(attendantName || accountName) && (
            <div className="hidden min-w-0 text-right lg:block">
              {attendantName ? (
                <p
                  className="max-w-[180px] truncate text-xs font-semibold text-foreground xl:max-w-[220px] xl:text-sm"
                  title={attendantName}
                >
                  {attendantName}
                </p>
              ) : null}
              {accountName ? (
                <p
                  className="max-w-[180px] truncate text-[11px] text-muted-foreground xl:max-w-[220px]"
                  title={accountName}
                >
                  Conta: {accountName}
                </p>
              ) : null}
            </div>
          )}
          <button
            type="button"
            onClick={toggleTheme}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground touch-manipulation"
            aria-label={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          >
            {theme === 'dark' ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>
          <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1 text-[11px] font-medium text-foreground lg:hidden">
            <Coins className="h-3.5 w-3.5 text-primary" />
            <span>{formatCredits(creditsBalance)}</span>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground touch-manipulation sm:px-3"
          >
            <LogOut className="h-[18px] w-[18px]" aria-hidden />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>
      </div>
    </header>
  )
}
